// Analytics Dashboard -  Architecture BRIO
// Decision-support system with 15 historical KPIs
// Design: Calm, Precise, Architectural
//
// ============================================================================
// FIXES APPLIED (January 2026):
// ============================================================================
// Fix 1: Extracted hooks from IIFEs in OverviewSection (selectedMetric, distMode)
//        to top level of OverviewSection component
// Fix 2: Extracted hooks from IIFEs in ProjectReportsSection 
//        (selectedProjectForDrilldown, projectProfitabilityData) to top level
// Fix 3: Defined avgFeePerHour locally in ProjectReportsSection for use in
//        "effective fee / hour" chart comparison
// Fix 4: Replaced all uses of timeEntries with filteredEntries in 
//        ProjectReportsSection charts (margin erosion, effective fee/hour, 
//        fee per m² vs margin)
// Fix 5: Added division-by-zero guards for weighted-average calculations
// Fix 6: Added parseInt NaN guard in project select dropdown
// Fix 7: Wrapped all localStorage.setItem calls in try/catch blocks
// Fix 8: Replaced alert/confirm calls with non-blocking UX patterns
//        (validation messages, inline confirmations)
// Fix 9: Integrated BRIO project color palettes from brioProjectColors.css
//        - Added helper functions: parseCssPalette, getBrioPalettes, 
//          getProjectColor, getCategoryColorByIndex, getCategoryChartColors
//        - Project charts now use project.color when available, falling back
//          to BRIO category palettes from CSS variables
//        - Category distribution charts use full BRIO palette colors
// ============================================================================

import React, { useState, useEffect, useMemo, useCallback, Suspense } from 'react';
import {
  BarChart, Bar, Line, PieChart as RechartsPie, Pie, Cell, ComposedChart, ReferenceLine, ReferenceArea,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';

import { useSalaryLedger } from '../../hooks/useSalaryLedger';
import { useAllRoleHistory } from '../../hooks/useAllRoleHistory';
import { supabase } from '../../supabaseClient';
import { brioColors, brioStyles, getCategoryChartColors } from './shared/theme';
import { CustomTooltip, ChartExpandWrapper, _fmtCurrFallback } from './shared/components';
import { AnalyticsContext } from './shared/AnalyticsContext';
// Section components are code-split: only the active tab's chunk is fetched,
// keeping the big sections (cashflow ~2.4k lines) out of the initial bundle.
const TimesheetsSection = React.lazy(() => import('./sections/TimesheetsSection'));
const OverviewSection = React.lazy(() => import('./sections/OverviewSection'));
const SalaryCostAnalysisSection = React.lazy(() => import('./sections/SalaryCostAnalysisSection'));
const StageIntelligenceSection = React.lazy(() => import('./sections/StageIntelligenceSection'));
const CapacityIntelligenceSection = React.lazy(() => import('./sections/CapacityIntelligenceSection'));
const TeamAllocationSection = React.lazy(() => import('./sections/TeamAllocationSection'));
const ProjectReportsSection = React.lazy(() => import('./sections/ProjectReportsSection'));
const InvoicingSection = React.lazy(() => import('./sections/InvoicingSection'));
const CashflowProjectionSection = React.lazy(() => import('./sections/CashflowProjectionSection'));
const FinancialOverviewSection = React.lazy(() => import('./sections/FinancialOverviewSection'));

// cashflowHelpers moved to ./shared/calc.js

// ============================================================================
// BRIO DESIGN SYSTEM
// ============================================================================

// buildRoleSegments moved to ./shared/calc.js

// Theme tokens, BRIO color system and the linearRegression helper now live in ./shared/theme.js

// ============================================================================
// EXPORT FUNCTIONS
// ============================================================================

// Shared presentational components moved to ./shared/components.jsx
export default function AnalyticsDashboard({
  timeEntries = [],
  projects = [],
  users = [],
  clients = [],
  invoices = [],
  allocations = [],
  // Current authenticated user object (from App.js).
  currentUser = null,
  // Supabase-backed invoices across all projects (from invoicesHook or equivalent).
  allProjectInvoices = [],
  // Invoice hook for fetching all invoices
  invoicesHook = null,
  // Backward-compatible extension for partial stage invoicing
  stageStatusHook = null,
  // Centralized settings hook (from useSettings, passed via App.js)
  settingsHook = null,
  // Project expenses and office overheads for actual overhead calculation
  activeExpenses = [],
  activeOfficeOverheads = [],
  // Centralized settings (read-only from useSettings hook, passed via App.js)
  essentialProfitPercentage = 20,
  // Overhead allocation (management + support untracked salary costs)
  computeOverheadAllocation = null,
  // Currency formatting (from App.js settingsHook.formatMoney)
  fmtCurr: fmtCurrProp = null,
  currSymbol: currSymbolProp = '₹',
  // Project status filter from header menu (App.js)
  projectStatusFilter = 'default',
  // Whether the full time-entry history has finished loading (App.js paginates
  // it in). KPI trend sparklines wait for this so early years aren't missing.
  allTimeEntriesLoaded = true,
  // Active section controlled by App.js subheader
  activeSection: activeSectionProp,
  onSectionChange
}) {
  // ========================================================================
  // CURRENCY DISPLAY HELPERS (Option 1: Display-only conversion)
  // ========================================================================
  // All stored amounts are in baseCurrency (INR). When displayCurrency differs,
  // we convert using FX rates at display time only.
  // ========================================================================
  
  // Helper to get FX date from month key (e.g., "2025-01" -> "2025-01-01")
  const getMonthFxDate = (monthKey) => {
    if (!monthKey) return null;
    // If already YYYY-MM-DD format, return as-is
    if (monthKey.length === 10) return monthKey;
    // If YYYY-MM format, append -01
    if (monthKey.length === 7) return `${monthKey}-01`;
    return null;
  };
  
  // Format money with conversion to display currency
  // Uses settingsHook.formatMoneyInDisplayCurrency for proper FX conversion
  const money = (amount, fxDate, sourceCurrency) => {
    if (settingsHook && settingsHook.formatMoneyInDisplayCurrency) {
      return settingsHook.formatMoneyInDisplayCurrency(
        amount || 0,
        sourceCurrency || settingsHook.baseCurrency,
        fxDate
      );
    }
    // Fallback to fmtCurr prop (may not convert, just format)
    if (fmtCurrProp) {
      return fmtCurrProp(amount || 0);
    }
    // Ultimate fallback to INR
    return _fmtCurrFallback(amount || 0);
  };
  
  // Legacy fmtCurr - now delegates to money() for conversion
  // Kept for backward compatibility with existing code that doesn't pass dates
  const fmtCurr = (amount, fxDate) => money(amount, fxDate);
  
  // Currency symbol for labels (dynamically based on displayCurrency)
  const currSymbol = settingsHook?.displayCurrency === 'EUR' ? '€' : (currSymbolProp || '₹');

  // ========================================================================
  // PAID INVOICES: Fetch all from Supabase for revenue calculations
  // ========================================================================
  const [paidInvoicesAll, setPaidInvoicesAll] = useState([]);
  const [paidInvoicesLoaded, setPaidInvoicesLoaded] = useState(false);
  // Billed revenue per project = issued + paid + partially_paid (i.e. raised,
  // whether or not collected). Used as the revenue basis for project MARGIN so
  // ongoing/under-collected projects aren't shown as catastrophic losses.
  const [billedRevenueByProject, setBilledRevenueByProject] = useState({});

  useEffect(() => {
    let cancelled = false;
    const fetchAll = async () => {
      try {
        const { data, error } = await supabase
          .from('v_invoices_reporting')
          .select('id, project_id, invoice_date, base_amount, gross_amount, net_amount, base_amount_inr_default, currency, status, paid_confirmed, paid_date')
          .is('deleted_at', null);
        if (error) throw error;
        if (!cancelled) {
          setPaidInvoicesAll((data || []).filter(r => r.paid_confirmed).map(r => ({
            projectId: r.project_id,
            invoiceDate: r.invoice_date,
            netAmount: r.net_amount ?? 0,
            paidDate: r.paid_date ?? r.invoice_date,
            baseAmountInrDefault: r.base_amount_inr_default ?? null,
            currency: r.currency ?? 'INR',
          })));
          // Billed (raised) revenue per project: issued + paid + partially_paid.
          const RAISED = new Set(['issued', 'paid', 'partially_paid']);
          const billed = {};
          (data || []).forEach(r => {
            if (!RAISED.has(String(r.status || '').toLowerCase())) return;
            billed[r.project_id] = (billed[r.project_id] || 0) + (r.net_amount ?? 0);
          });
          setBilledRevenueByProject(billed);
          setPaidInvoicesLoaded(true);
        }
      } catch (err) {
        console.error('[AnalyticsDashboard] Error fetching invoices:', err);
        if (!cancelled) setPaidInvoicesLoaded(true);
      }
    };
    fetchAll();
    return () => { cancelled = true; };
  }, []);

  // Fetch all invoices on mount. fetchAllInvoices is a stable useCallback([])
  // reference, so this effect runs exactly once (no looping).
  // NOTE: Do NOT re-fetch on section change — that causes allInvoices state
  // updates mid-session which re-renders the parent, creating a new
  // CashflowProjectionSection function identity, causing React to unmount and
  // remount the section (closing any open dropdowns).
  const fetchAllInvoices = invoicesHook?.fetchAllInvoices;
  useEffect(() => {
    fetchAllInvoices?.();
  }, [fetchAllInvoices]);

  // Index: paid income per project (total)
  const paidIncomeByProject = useMemo(() => {
    const idx = {};
    paidInvoicesAll.forEach(inv => {
      idx[inv.projectId] = (idx[inv.projectId] || 0) + inv.netAmount;
    });
    return idx;
  }, [paidInvoicesAll]);

  // Index: actual project expenses (non-planned)
  const expensesByProject = useMemo(() => {
    const idx = {};
    activeExpenses.forEach(e => {
      if (e.status === 'planned') return;
      idx[e.projectId] = (idx[e.projectId] || 0) + (e.amount || 0);
    });
    return idx;
  }, [activeExpenses]);

  // Whether paid invoices are available as revenue source
  const hasPaidInvoiceData = paidInvoicesLoaded && paidInvoicesAll.length > 0;

  // Revenue per project: paid invoices when available, else time-entry-based revenue
  const revenueByProject = useMemo(() => {
    if (hasPaidInvoiceData) return paidIncomeByProject;
    // Fallback: aggregate e.revenue from time entries
    const idx = {};
    timeEntries.forEach(e => {
      idx[e.projectId] = (idx[e.projectId] || 0) + (e.revenue || 0);
    });
    return idx;
  }, [hasPaidInvoiceData, paidIncomeByProject, timeEntries]);

  // Total revenue across all projects (with same fallback)
  const totalRevenueAll = useMemo(() => {
    return Object.values(revenueByProject).reduce((s, v) => s + v, 0);
  }, [revenueByProject]);

  // Paid invoice revenue indexed by YYYY-MM (for monthly charts)
  const paidRevenueByMonth = useMemo(() => {
    const idx = {};
    paidInvoicesAll.forEach(inv => {
      const dateField = inv.paidDate || inv.invoiceDate;
      if (!dateField) return;
      const ymKey = dateField.substring(0, 7);
      idx[ymKey] = (idx[ymKey] || 0) + inv.netAmount;
    });
    return idx;
  }, [paidInvoicesAll]);

  // Paid invoice revenue indexed by (projectId, YYYY-MM)
  const paidRevenueByProjectMonth = useMemo(() => {
    const idx = {};
    paidInvoicesAll.forEach(inv => {
      const dateField = inv.paidDate || inv.invoiceDate;
      if (!dateField) return;
      const ymKey = dateField.substring(0, 7);
      const pid = inv.projectId;
      if (!idx[pid]) idx[pid] = {};
      idx[pid][ymKey] = (idx[pid][ymKey] || 0) + inv.netAmount;
    });
    return idx;
  }, [paidInvoicesAll]);

  // Paid invoice revenue indexed by calendar year (total and per-project). Used
  // by the KPI trend sparklines so their revenue basis matches the headline KPI
  // values, which prefer paid-invoice revenue over time-entry revenue when
  // available (see getFilteredTotalRevenue / getProjectRevenue).
  const paidRevenueByYear = useMemo(() => {
    const idx = {};
    paidInvoicesAll.forEach(inv => {
      const dateField = inv.paidDate || inv.invoiceDate;
      if (!dateField) return;
      const year = Number(dateField.substring(0, 4));
      if (!Number.isFinite(year)) return;
      idx[year] = (idx[year] || 0) + inv.netAmount;
    });
    return idx;
  }, [paidInvoicesAll]);

  const paidRevenueByProjectYear = useMemo(() => {
    const idx = {};
    paidInvoicesAll.forEach(inv => {
      const dateField = inv.paidDate || inv.invoiceDate;
      if (!dateField) return;
      const year = Number(dateField.substring(0, 4));
      if (!Number.isFinite(year)) return;
      const pid = inv.projectId;
      if (!idx[pid]) idx[pid] = {};
      idx[pid][year] = (idx[pid][year] || 0) + inv.netAmount;
    });
    return idx;
  }, [paidInvoicesAll]);

  // Use prop from App.js subheader if available, otherwise fall back to internal state
  const [internalActiveSection, setInternalActiveSection] = useState('overview');
  const activeSection = activeSectionProp || internalActiveSection;
  const setActiveSection = onSectionChange || setInternalActiveSection;
  const [activeTab, setActiveTab] = useState({});
  const [_activeFilters, _setActiveFilters] = useState([]); // eslint-disable-line no-unused-vars

  // Full role history for every employee (for role-segmented tenure bars in
  // the teams section). Keyed by user id string.
  const { roleHistoryByUser } = useAllRoleHistory();
  const [showInactiveHeatmap, setShowInactiveHeatmap] = useState(false);

  // Per-chart expand/fullscreen state (chart id string or null)
  const [expandedChart, setExpandedChart] = useState(null);

  // Escape key handler for exiting expanded chart mode
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && expandedChart !== null) {
        setExpandedChart(null);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [expandedChart]);

  // Expanded chart mode: add body class to hide main app navigation and prevent scroll
  useEffect(() => {
    if (expandedChart !== null) {
      document.body.classList.add('analytics-fullscreen-active');
      document.body.style.overflow = 'hidden';
    } else {
      document.body.classList.remove('analytics-fullscreen-active');
      document.body.style.overflow = '';
    }
    // Cleanup on unmount - ensure we restore normal state
    return () => {
      document.body.classList.remove('analytics-fullscreen-active');
      document.body.style.overflow = '';
    };
  }, [expandedChart]);
  
  // Date range filter state
  const [dateRange, setDateRange] = useState(() => {
    const fallback = {
      preset: 'last12months', // trailing-12-months default (replaces ytd)
      startDate: '',
      endDate: '',
      compareEnabled: false,
      compareStartDate: '',
      compareEndDate: ''
    };
    try {
      const stored = localStorage.getItem('analytics_dateRange');
      if (!stored) return fallback;
      const parsed = JSON.parse(stored);
      // Migrate the retired 'ytd' preset for returning users.
      if (parsed.preset === 'ytd') parsed.preset = 'last12months';
      return parsed;
    } catch {
      return fallback;
    }
  });
  
  // Derive project status from the header menu filter prop.
  // Header uses 'default' for "show all"; analytics logic checks against 'all'.
  const projectStatus = (projectStatusFilter === 'default' || !projectStatusFilter) ? 'all' : projectStatusFilter;
  
  // Annotation system state
  const [annotations, setAnnotations] = useState(() => {
    try {
      const stored = localStorage.getItem('analytics_annotations');
      // No seeded sample annotations — start empty so the timeline only ever
      // shows real, user-entered events.
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  });

  // Save date range to localStorage
  // FIX 7: Wrap localStorage.setItem in try/catch
  useEffect(() => {
    try {
      localStorage.setItem('analytics_dateRange', JSON.stringify(dateRange));
    } catch (e) {
      console.warn('Failed to save date range to localStorage:', e);
    }
  }, [dateRange]);

  // Save annotations to localStorage
  // FIX 7: Wrap localStorage.setItem in try/catch
  useEffect(() => {
    try {
      localStorage.setItem('analytics_annotations', JSON.stringify(annotations));
    } catch (e) {
      console.warn('Failed to save annotations to localStorage:', e);
    }
  }, [annotations]);

  // ── Salary Ledger (Phase 2) ──
  const salaryLedgerHook = useSalaryLedger();
  const [salaryLedgerRows, setSalaryLedgerRows] = useState([]);

  useEffect(() => {
    const currentYear = new Date().getFullYear();
    salaryLedgerHook.getAllForYear(currentYear).then(rows => {
      setSalaryLedgerRows(rows || []);
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Build lookup: { authUserId: { totalSalary, totalBonus, months } }
  const salaryLedgerByUser = useMemo(() => {
    const map = {};
    salaryLedgerRows.forEach(row => {
      if (!map[row.auth_user_id]) {
        map[row.auth_user_id] = { totalSalary: 0, totalBonus: 0, months: 0 };
      }
      map[row.auth_user_id].totalSalary += (row.base_salary || 0);
      map[row.auth_user_id].totalBonus += (row.bonus || 0);
      map[row.auth_user_id].months += 1;
    });
    return map;
  }, [salaryLedgerRows]);

  // Monthly cost from ledger (average of available months), fallback to costRate
  const getLedgerMonthlyCost = useCallback((user) => {
    const ledger = user.authUserId ? salaryLedgerByUser[user.authUserId] : null;
    if (ledger && ledger.months > 0) {
      return (ledger.totalSalary + ledger.totalBonus) / ledger.months;
    }
    return user.costRate || 0;
  }, [salaryLedgerByUser]);

  // Date range calculation helper
  const getDateRangeBounds = (preset = dateRange.preset) => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    let start, end;

    switch (preset) {
      case 'today':
        start = end = today;
        break;
      case 'yesterday':
        start = end = new Date(today.getTime() - 86400000);
        break;
      case 'last7days':
        start = new Date(today.getTime() - 6 * 86400000);
        end = today;
        break;
      case 'last30days':
        start = new Date(today.getTime() - 29 * 86400000);
        end = today;
        break;
      case 'thisMonth':
        start = new Date(now.getFullYear(), now.getMonth(), 1);
        end = today;
        break;
      case 'lastMonth':
        start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        end = new Date(now.getFullYear(), now.getMonth(), 0);
        break;
      case 'thisQuarter': {
        const quarterStartMonth = Math.floor(now.getMonth() / 3) * 3;
        start = new Date(now.getFullYear(), quarterStartMonth, 1);
        end = today;
        break;
      }
      case 'lastQuarter': {
        const lastQuarterStartMonth = Math.floor(now.getMonth() / 3) * 3 - 3;
        start = new Date(now.getFullYear(), lastQuarterStartMonth, 1);
        end = new Date(now.getFullYear(), lastQuarterStartMonth + 3, 0);
        break;
      }
      case 'last12months':
        start = new Date(now.getFullYear() - 1, now.getMonth(), now.getDate());
        end = today;
        break;
      case 'lastYear':
        start = new Date(now.getFullYear() - 1, 0, 1);
        end = new Date(now.getFullYear() - 1, 11, 31);
        break;
      case 'last5years':
        start = new Date(now.getFullYear() - 5, 0, 1);
        end = today;
        break;
      case 'custom':
        start = dateRange.startDate ? new Date(dateRange.startDate) : new Date(now.getFullYear(), 0, 1);
        end = dateRange.endDate ? new Date(dateRange.endDate) : today;
        break;
      case 'allTime':
      default:
        // Floor at the first year of dense data (2018). 2015–17 hold only a
        // few hundred scattered entries and would skew any "all time" trend.
        start = new Date(2018, 0, 1);
        end = today;
        break;
    }

    return {
      start: start.toISOString().split('T')[0],
      end: end.toISOString().split('T')[0]
    };
  };

  // Overhead allocation per project (shared by all chart sections).
  // Calls computeOverheadAllocation with the date-range months so that
  // management + support untracked costs are distributed to projects the
  // same way as in ProjectProfitabilityPage.
  const overheadByProject = useMemo(() => {
    if (!computeOverheadAllocation) return {};
    const bounds = getDateRangeBounds();
    const startDate = new Date(bounds.start);
    const endDate = new Date(bounds.end);
    const months = [];
    let y = startDate.getFullYear(), m = startDate.getMonth() + 1;
    const ey = endDate.getFullYear(), em = endDate.getMonth() + 1;
    while (y < ey || (y === ey && m <= em)) {
      months.push(`${y}-${String(m).padStart(2, '0')}`);
      m++;
      if (m > 12) { m = 1; y++; }
    }
    if (months.length === 0) return {};
    const result = computeOverheadAllocation(months);
    // Build a simple { projectId: totalOverhead } map
    const map = {};
    if (result && result.byProject) {
      Object.entries(result.byProject).forEach(([pid, data]) => {
        map[pid] = data.totalOverhead || 0;
      });
    }
    return map;
  }, [computeOverheadAllocation, dateRange]); // eslint-disable-line react-hooks/exhaustive-deps

  // Filter data by date range. "All time" still applies the 2018+ floor so the
  // sparse 2015–17 tail never leaks into long-range views.
  const filterByDateRange = (data, dateField = 'date') => {
    const bounds = getDateRangeBounds();
    return data.filter(item => {
      const itemDate = item[dateField];
      if (!itemDate) return false;
      return itemDate >= bounds.start && itemDate <= bounds.end;
    });
  };

  // Filter data by project status
  const filterByProjectStatus = (entries) => {
    if (projectStatus === 'all') return entries;
    
    return entries.filter(entry => {
      const project = projects.find(p => p.id === entry.projectId);
      return project && project.status === projectStatus;
    });
  };

  // Combined filter: date range + project status
  const filterEntries = (data, dateField = 'date') => {
    let filtered = filterByDateRange(data, dateField);
    filtered = filterByProjectStatus(filtered);
    return filtered;
  };

  // Filter projects by status
  const filterProjects = (projectsList) => {
    if (projectStatus === 'all') return projectsList;
    return projectsList.filter(p => p.status === projectStatus);
  };

  // Get comparison period data
  const getComparisonData = (data, dateField = 'date') => {
    if (!dateRange.compareEnabled || !dateRange.compareStartDate || !dateRange.compareEndDate) {
      return null;
    }

    return data.filter(item => {
      const itemDate = item[dateField];
      if (!itemDate) return false;
      return itemDate >= dateRange.compareStartDate && itemDate <= dateRange.compareEndDate;
    });
  };

  // ========================================================================
  // DATE-RANGE-FILTERED REVENUE FROM PAID INVOICES
  // ========================================================================
  // These mirror filterEntries() but for paid invoices, applying both date
  // range and project-status filters so KPIs stay consistent.
  // ========================================================================

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const filteredPaidRevenue = useMemo(() => {
    if (!hasPaidInvoiceData) return null;
    const bounds = getDateRangeBounds();
    let total = 0;
    paidInvoicesAll.forEach(inv => {
      const dateField = inv.paidDate || inv.invoiceDate;
      if (!dateField) return;
      if (dateField < bounds.start || dateField > bounds.end) return;
      if (projectStatus !== 'all') {
        const project = projects.find(p => p.id === inv.projectId);
        if (!project || project.status !== projectStatus) return;
      }
      total += inv.netAmount;
    });
    return total;
  }, [hasPaidInvoiceData, paidInvoicesAll, dateRange, projectStatus, projects]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const filteredRevenueByProject = useMemo(() => {
    if (!hasPaidInvoiceData) return null;
    const bounds = getDateRangeBounds();
    const idx = {};
    paidInvoicesAll.forEach(inv => {
      const dateField = inv.paidDate || inv.invoiceDate;
      if (!dateField) return;
      if (dateField < bounds.start || dateField > bounds.end) return;
      if (projectStatus !== 'all') {
        const project = projects.find(p => p.id === inv.projectId);
        if (!project || project.status !== projectStatus) return;
      }
      idx[inv.projectId] = (idx[inv.projectId] || 0) + inv.netAmount;
    });
    return idx;
  }, [hasPaidInvoiceData, paidInvoicesAll, dateRange, projectStatus, projects]);

  // Comparison-period paid revenue (for KPI comparison tiles)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const comparisonPaidRevenue = useMemo(() => {
    if (!hasPaidInvoiceData) return null;
    if (!dateRange.compareEnabled || !dateRange.compareStartDate || !dateRange.compareEndDate) return null;
    let total = 0;
    paidInvoicesAll.forEach(inv => {
      const dateField = inv.paidDate || inv.invoiceDate;
      if (!dateField) return;
      if (dateField < dateRange.compareStartDate || dateField > dateRange.compareEndDate) return;
      if (projectStatus !== 'all') {
        const project = projects.find(p => p.id === inv.projectId);
        if (!project || project.status !== projectStatus) return;
      }
      total += inv.netAmount;
    });
    return total;
  }, [hasPaidInvoiceData, paidInvoicesAll, dateRange, projectStatus, projects]);

  // Helper: get revenue for a specific project (date-range-filtered, with fallback)
  const getProjectRevenue = (projectId, fallbackEntries) => {
    if (filteredRevenueByProject) return filteredRevenueByProject[projectId] || 0;
    if (fallbackEntries) return fallbackEntries.reduce((sum, e) => sum + (e.revenue || 0), 0);
    return revenueByProject[projectId] || 0;
  };

  // Helper: total filtered revenue (with fallback to e.revenue)
  const getFilteredTotalRevenue = (entries) => {
    if (filteredPaidRevenue != null) return filteredPaidRevenue;
    return entries.reduce((sum, e) => sum + (e.revenue || 0), 0);
  };

  const handleAddAnnotation = (annotation) => {
    setAnnotations([...annotations, annotation]);
  };

  // FIX 8: State for pending delete confirmation
  const [pendingDeleteId, setPendingDeleteId] = useState(null);

  // FIX 8: Non-blocking delete with inline confirmation
  const handleDeleteAnnotation = (id) => {
    if (pendingDeleteId === id) {
      // Second click confirms deletion
      setAnnotations(annotations.filter(a => a.id !== id));
      setPendingDeleteId(null);
    } else {
      // First click sets pending state
      setPendingDeleteId(id);
      // Auto-reset after 3 seconds if not confirmed
      setTimeout(() => setPendingDeleteId((current) => current === id ? null : current), 3000);
    }
  };

  const handleToggleAnnotation = (id) => {
    setAnnotations(annotations.map(a => 
      a.id === id ? { ...a, enabled: !a.enabled } : a
    ));
  };

  // Get enabled annotations for a date range
  const _getAnnotationsForRange = (startDate, endDate) => { // eslint-disable-line no-unused-vars
    return annotations.filter(a => {
      if (!a.enabled) return false;
      const annDate = new Date(a.date);
      const start = new Date(startDate);
      const end = new Date(endDate);
      return annDate >= start && annDate <= end;
    });
  };

  // Export functions
  const handleExportCSV = () => {
    const filteredData = filterEntries(timeEntries);
    const bounds = getDateRangeBounds();
    
    // CSV Header
    const headers = [
      'Date',
      'User',
      'Project',
      'Client',
      'Task',
      'Description',
      'Hours',
      'Billable',
      'Revenue',
      'Cost',
      'Margin'
    ];

    // CSV Rows
    const rows = filteredData.map(entry => {
      const user = users.find(u => u.id === entry.userId);
      const project = projects.find(p => p.id === entry.projectId);
      const client = clients.find(c => c.id === project?.clientId);
      const hours = (entry.duration / 3600).toFixed(2);
      const revenue = (entry.revenue || 0).toFixed(2);
      const cost = (entry.cost || 0).toFixed(2);
      const margin = entry.revenue > 0 ? (((entry.revenue - entry.cost) / entry.revenue) * 100).toFixed(1) : '0';

      return [
        entry.date,
        user?.name || '',
        project?.name || '',
        client?.name || '',
        entry.taskId || '',
        `"${(entry.description || '').replace(/"/g, '""')}"`,
        hours,
        entry.billable ? 'Yes' : 'No',
        revenue,
        cost,
        margin + '%'
      ].join(',');
    });

    // Combine
    const csv = [headers.join(','), ...rows].join('\n');

    // Download
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `analytics-export-${bounds.start}-to-${bounds.end}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const handleExportPDF = () => {
    // Create HTML content for PDF
    const filteredData = filterEntries(timeEntries);
    const bounds = getDateRangeBounds();
    
    // Calculate summary statistics
    const totalHours = filteredData.reduce((sum, e) => sum + (e.duration / 3600), 0);
    const totalRevenue = getFilteredTotalRevenue(filteredData);
    const totalCost = filteredData.reduce((sum, e) => sum + (e.cost || 0), 0);
    const totalMargin = totalRevenue > 0 ? ((totalRevenue - totalCost) / totalRevenue * 100) : 0;
    const billableHours = filteredData.filter(e => e.billable).reduce((sum, e) => sum + (e.duration / 3600), 0);
    const billableUtil = totalHours > 0 ? (billableHours / totalHours * 100) : 0;

    const htmlContent = `
<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <title>Analytics Report</title>
  <style>
    body {
      font-family: 'Helvetica', 'Arial', sans-serif;
      margin: 40px;
      color: #111;
      font-size: 11px;
    }
    h1 {
      font-size: 24px;
      font-weight: 400;
      margin-bottom: 8px;
      text-transform: lowercase;
    }
    h2 {
      font-size: 15px;
      font-weight: 400;
      margin-top: 24px;
      margin-bottom: 12px;
      text-transform: lowercase;
      border-bottom: 1px solid #e5e5e5;
      padding-bottom: 4px;
    }
    .meta {
      color: #666;
      font-size: 11px;
      margin-bottom: 24px;
      font-style: italic;
    }
    .summary {
      display: grid;
      grid-template-columns: repeat(3, 1fr);
      gap: 16px;
      margin-bottom: 32px;
    }
    .kpi {
      border: 1px solid #e5e5e5;
      padding: 16px;
    }
    .kpi-label {
      color: #666;
      font-size: 10px;
      text-transform: lowercase;
      margin-bottom: 4px;
    }
    .kpi-value {
      font-size: 24px;
      font-weight: 400;
      color: #111;
    }
    table {
      width: 100%;
      border-collapse: collapse;
      margin-top: 16px;
    }
    th, td {
      text-align: left;
      padding: 8px;
      border-bottom: 1px solid #e5e5e5;
      font-size: 10px;
    }
    th {
      font-weight: 400;
      color: #666;
      text-transform: lowercase;
    }
    .footer {
      margin-top: 48px;
      padding-top: 16px;
      border-top: 1px solid #e5e5e5;
      color: #666;
      font-size: 10px;
      text-align: center;
    }
  </style>
</head>
<body>
  <h1>analytics report</h1>
  <div class="meta">
    exported: ${new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}<br>
    period: ${new Date(bounds.start).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} – 
    ${new Date(bounds.end).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
  </div>

  <h2>summary</h2>
  <div class="summary">
    <div class="kpi">
      <div class="kpi-label">total hours</div>
      <div class="kpi-value">${Math.round(totalHours)}h</div>
    </div>
    <div class="kpi">
      <div class="kpi-label">total revenue</div>
      <div class="kpi-value">${fmtCurr(totalRevenue)}</div>
    </div>
    <div class="kpi">
      <div class="kpi-label">margin</div>
      <div class="kpi-value">${Math.abs(totalMargin) < 10 ? totalMargin.toFixed(1) : Math.round(totalMargin)}%</div>
    </div>
    <div class="kpi">
      <div class="kpi-label">billable utilization</div>
      <div class="kpi-value">${Math.abs(billableUtil) < 10 ? billableUtil.toFixed(1) : Math.round(billableUtil)}%</div>
    </div>
    <div class="kpi">
      <div class="kpi-label">total cost</div>
      <div class="kpi-value">${fmtCurr(totalCost)}</div>
    </div>
    <div class="kpi">
      <div class="kpi-label">entries</div>
      <div class="kpi-value">${filteredData.length}</div>
    </div>
  </div>

  <h2>detailed entries</h2>
  <table>
    <thead>
      <tr>
        <th>date</th>
        <th>user</th>
        <th>project</th>
        <th>hours</th>
        <th>billable</th>
        <th>revenue</th>
        <th>margin</th>
      </tr>
    </thead>
    <tbody>
      ${filteredData.slice(0, 100).map(entry => {
        const user = users.find(u => u.id === entry.userId);
        const project = projects.find(p => p.id === entry.projectId);
        const hours = Math.round(entry.duration / 3600);
        const marginVal = entry.revenue > 0 ? ((entry.revenue - entry.cost) / entry.revenue) * 100 : 0;
        const margin = Math.abs(marginVal) < 10 ? marginVal.toFixed(1) : String(Math.round(marginVal));
        
        return `
          <tr>
            <td>${entry.date}</td>
            <td>${user?.name || ''}</td>
            <td>${project?.name || ''}</td>
            <td>${hours}h</td>
            <td>${entry.billable ? 'yes' : 'no'}</td>
            <td>${fmtCurr(entry.revenue || 0)}</td>
            <td>${margin}%</td>
          </tr>
        `;
      }).join('')}
    </tbody>
  </table>
  ${filteredData.length > 100 ? `<p style="margin-top: 16px; color: #666; font-size: 10px; font-style: italic;">showing first 100 of ${filteredData.length} entries</p>` : ''}

  <div class="footer">
    architecture brio analytics dashboard
  </div>
</body>
</html>
    `;

    // Create blob and download
    const blob = new Blob([htmlContent], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `analytics-report-${bounds.start}-to-${bounds.end}.html`;
    link.click();
    URL.revokeObjectURL(url);
    
    // FIX 8: Use state-based notification instead of alert
    // Note: Actual PDF generation would require a library like jsPDF or html2pdf
    // This exports as HTML which can be printed to PDF by the user
    setExportNotification('Report exported as HTML. Open the file and use your browser\'s Print to PDF feature to save as PDF.');
  };

  // FIX 8: State for export notification
  const [exportNotification, setExportNotification] = useState('');

  // ============================================================================
  // SECTION 1: OVERVIEW
  // ============================================================================

  // OverviewSection extracted to ./sections/OverviewSection.jsx

  // ============================================================================
  // SECTION 2: TIMESHEETS
  // ============================================================================

  // TimesheetsSection extracted to ./sections/TimesheetsSection.jsx

  // ============================================================================
  // SECTION 3: TEAM ALLOCATION
  // ============================================================================

  // TeamAllocationSection extracted to ./sections/TeamAllocationSection.jsx

  // ============================================================================
  // CATEGORY PERFORMANCE CHART COMPONENT
  // ============================================================================

  const CategoryPerformanceChart = ({ filteredEntries }) => {
    const categoryStats = useMemo(() => {
      const filteredProjects = filterProjects(projects);
      const categories = [...new Set(filteredProjects.map(p => p.category || 'uncategorized'))];
      
      const stats = categories.map(category => {
        const catProjects = filteredProjects.filter(p => (p.category || 'uncategorized') === category);
        const catProjectIds = catProjects.map(p => p.id);
        const catEntries = filteredEntries.filter(e => catProjectIds.includes(e.projectId));
        
        const hours = catEntries.reduce((sum, e) => sum + (e.duration / 3600), 0);
        const revenue = hasPaidInvoiceData
          ? catProjectIds.reduce((sum, pid) => sum + (revenueByProject[pid] || 0), 0)
          : catEntries.reduce((sum, e) => sum + (e.revenue || 0), 0);
        const cost = catEntries.reduce((sum, e) => sum + (e.cost || 0), 0);
        const profit = revenue - cost;
        const margin = revenue > 0 ? ((profit / revenue) * 100) : 0;
        
        return {
          category: category.charAt(0).toUpperCase() + category.slice(1),
          projects: catProjects.length,
          hours: parseFloat(hours.toFixed(1)),
          revenue: parseFloat(revenue.toFixed(0)),
          cost: parseFloat(cost.toFixed(0)),
          profit: parseFloat(profit.toFixed(0)),
          margin: parseFloat(margin.toFixed(1))
        };
      }).filter(c => c.hours > 0).sort((a, b) => b.revenue - a.revenue);
      
      return stats;
    }, [filteredEntries, projects, projectStatus]);

    // Generate colors from BRIO category palettes
    const colors = useMemo(() => {
      return getCategoryChartColors(categoryStats.map(c => c.category.toLowerCase()));
    }, [categoryStats]);

    if (categoryStats.length === 0) {
      return (
        <div style={{
          background: brioColors.white,
          border: `1px solid ${brioColors.border}`,
          padding: brioStyles.spacingM,
          marginBottom: brioStyles.spacingM
        }}>
          <h3 style={{
            fontSize: brioStyles.fontSizeH2,
            fontWeight: brioStyles.fontWeightMedium,
            color: brioColors.black,
            marginBottom: brioStyles.spacingS,
            textTransform: 'lowercase'
          }}>
            performance by project category
          </h3>
          <p style={{
            fontSize: brioStyles.fontSizeBase,
            color: brioColors.grey,
            fontStyle: 'italic'
          }}>
            no category data available for the selected period
          </p>
        </div>
      );
    }

    return (
      <div style={{
        background: brioColors.white,
        border: `1px solid ${brioColors.border}`,
        padding: brioStyles.spacingM,
        marginBottom: brioStyles.spacingM
      }}>
        <h3 style={{
          fontSize: brioStyles.fontSizeH2,
          fontWeight: brioStyles.fontWeightMedium,
          color: brioColors.black,
          marginBottom: '8px',
          textTransform: 'lowercase'
        }}>
          performance by project category
        </h3>
        <p style={{
          fontSize: '11px',
          color: brioColors.grey,
          fontWeight: brioStyles.fontWeightLight,
          marginBottom: brioStyles.spacingM,
          fontStyle: 'italic'
        }}>
          comparative analysis of project types
        </p>

        {/* Summary Stats */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
          gap: brioStyles.spacingS,
          marginBottom: brioStyles.spacingL,
          padding: brioStyles.spacingS,
          background: brioColors.greyLight
        }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '20px', fontWeight: brioStyles.fontWeightMedium, color: brioColors.black }}>
              {categoryStats.length}
            </div>
            <div style={{ fontSize: '11px', color: brioColors.grey }}>categories</div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '20px', fontWeight: brioStyles.fontWeightMedium, color: brioColors.black }}>
              {categoryStats.reduce((s, c) => s + c.projects, 0)}
            </div>
            <div style={{ fontSize: '11px', color: brioColors.grey }}>projects</div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '20px', fontWeight: brioStyles.fontWeightMedium, color: brioColors.gold }}>
              {fmtCurr(categoryStats.reduce((s, c) => s + c.revenue, 0))}
            </div>
            <div style={{ fontSize: '11px', color: brioColors.grey }}>total revenue</div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '20px', fontWeight: brioStyles.fontWeightMedium, color: brioColors.black }}>
              {/* FIX 5: Added division-by-zero guard */}
              {((() => {
                const totalRevenue = categoryStats.reduce((s, c) => s + c.revenue, 0);
                if (totalRevenue <= 0) return '0%';
                const avg = categoryStats.reduce((s, c) => s + c.margin * c.revenue, 0) / totalRevenue;
                return Math.abs(avg) < 10 ? `${avg.toFixed(1)}%` : `${Math.round(avg)}%`;
              })())}
            </div>
            <div style={{ fontSize: '11px', color: brioColors.grey }}>avg margin</div>
          </div>
        </div>

        {/* Revenue & Margin Chart */}
        <ChartExpandWrapper id="category-revenue-margin" expandedChart={expandedChart} setExpandedChart={setExpandedChart} title="revenue & margin by category">
        <div style={{ marginBottom: brioStyles.spacingL }}>
          <h4 style={{
            fontSize: brioStyles.fontSizeBase,
            fontWeight: brioStyles.fontWeightMedium,
            color: brioColors.black,
            marginBottom: brioStyles.spacingS,
            textTransform: 'lowercase'
          }}>
            revenue & margin by category
          </h4>
          <ResponsiveContainer width="100%" height={280}>
            <ComposedChart data={categoryStats} margin={{ left: 10, right: 10 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={brioColors.border} />
              <XAxis
                dataKey="category"
                stroke={brioColors.grey}
                style={{ fontSize: '11px', fontFamily: brioStyles.fontFamily }}
                scale="band"
              />
              <YAxis
                yAxisId="left"
                stroke={brioColors.grey}
                style={{ fontSize: '11px', fontFamily: brioStyles.fontFamily }}
              />
              <YAxis
                yAxisId="right"
                orientation="right"
                stroke={brioColors.grey}
                style={{ fontSize: '11px', fontFamily: brioStyles.fontFamily }}
              />
              <Tooltip content={<CustomTooltip />} cursor={false} />
              <Legend wrapperStyle={{ fontSize: '11px', fontFamily: brioStyles.fontFamily }} />
              <Bar yAxisId="left" dataKey="revenue" fill={brioColors.gold} name={`revenue (${currSymbol})`} activeBar={false} />
              <Line yAxisId="right" type="linear" dataKey="margin" stroke={brioColors.black} strokeWidth={2} name="margin %" connectNulls />
              {/* Essential profit target band on margin axis */}
              <ReferenceArea
                yAxisId="right"
                y1={0}
                y2={essentialProfitPercentage}
                fill={brioColors.gold}
                fillOpacity={0.07}
                stroke="none"
              />
              <ReferenceLine
                yAxisId="right"
                y={essentialProfitPercentage}
                stroke={brioColors.gold}
                strokeOpacity={0.4}
                strokeDasharray="4 3"
                label={{
                  value: `${essentialProfitPercentage}% target`,
                  position: 'right',
                  style: { fontSize: '9px', fill: brioColors.grey, fontFamily: brioStyles.fontFamily, fontStyle: 'italic' }
                }}
              />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
        </ChartExpandWrapper>

        {/* Profitability Breakdown */}
        <ChartExpandWrapper id="category-profitability" expandedChart={expandedChart} setExpandedChart={setExpandedChart} title="profitability breakdown">
        <div style={{ marginBottom: brioStyles.spacingL }}>
          <h4 style={{
            fontSize: brioStyles.fontSizeBase,
            fontWeight: brioStyles.fontWeightMedium,
            color: brioColors.black,
            marginBottom: brioStyles.spacingS,
            textTransform: 'lowercase'
          }}>
            profitability breakdown
          </h4>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={categoryStats}>
              <CartesianGrid strokeDasharray="3 3" stroke={brioColors.border} />
              <XAxis
                dataKey="category"
                stroke={brioColors.grey}
                style={{ fontSize: '11px', fontFamily: brioStyles.fontFamily }}
              />
              <YAxis
                stroke={brioColors.grey}
                style={{ fontSize: '11px', fontFamily: brioStyles.fontFamily }}
              />
              <Tooltip content={<CustomTooltip />} cursor={false} />
              <Legend wrapperStyle={{ fontSize: '11px', fontFamily: brioStyles.fontFamily }} />
              <Bar dataKey="revenue" stackId="a" fill={brioColors.gold} name="revenue" activeBar={false} />
              <Bar dataKey="cost" stackId="a" fill={brioColors.goldMedium} name="cost" activeBar={false} />
              <Bar dataKey="profit" fill="#969e78" name="profit" activeBar={false} />
            </BarChart>
          </ResponsiveContainer>
        </div>
        </ChartExpandWrapper>

        {/* Distribution Pie Charts */}
        <ChartExpandWrapper id="category-distribution-pies" expandedChart={expandedChart} setExpandedChart={setExpandedChart} title="revenue & hours distribution">
        <div style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: brioStyles.spacingM,
          marginBottom: brioStyles.spacingL
        }}>
          <div>
            <h4 style={{
              fontSize: brioStyles.fontSizeBase,
              fontWeight: brioStyles.fontWeightMedium,
              color: brioColors.black,
              marginBottom: brioStyles.spacingS,
              textTransform: 'lowercase'
            }}>
              revenue distribution
            </h4>
            <ResponsiveContainer width="100%" height={360}>
              <RechartsPie>
                <Pie
                  data={categoryStats}
                  dataKey="revenue"
                  nameKey="category"
                  cx="50%"
                  cy="50%"
                  outerRadius={140}
                  label={false}
                >
                  {categoryStats.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={colors[index % colors.length]} />
                  ))}
                </Pie>
                <Tooltip content={<CustomTooltip />} cursor={false} />
              </RechartsPie>
            </ResponsiveContainer>
          </div>

          <div>
            <h4 style={{
              fontSize: brioStyles.fontSizeBase,
              fontWeight: brioStyles.fontWeightMedium,
              color: brioColors.black,
              marginBottom: brioStyles.spacingS,
              textTransform: 'lowercase'
            }}>
              hours distribution
            </h4>
            <ResponsiveContainer width="100%" height={360}>
              <RechartsPie>
                <Pie
                  data={categoryStats}
                  dataKey="hours"
                  nameKey="category"
                  cx="50%"
                  cy="50%"
                  outerRadius={140}
                  label={false}
                >
                  {categoryStats.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={colors[index % colors.length]} />
                  ))}
                </Pie>
                <Tooltip content={<CustomTooltip />} cursor={false} />
              </RechartsPie>
            </ResponsiveContainer>
          </div>
        </div>
        </ChartExpandWrapper>

        {/* Data Table */}
        <div style={{ overflowX: 'auto' }}>
          <h4 style={{
            fontSize: brioStyles.fontSizeBase,
            fontWeight: brioStyles.fontWeightMedium,
            color: brioColors.black,
            marginBottom: brioStyles.spacingS,
            textTransform: 'lowercase'
          }}>
            detailed metrics
          </h4>
          <table style={{
            width: '100%',
            borderCollapse: 'collapse',
            fontSize: '11px'
          }}>
            <thead>
              <tr style={{ background: brioColors.greyLight, borderBottom: `2px solid ${brioColors.border}` }}>
                <th style={{ padding: brioStyles.spacingS, textAlign: 'left', fontWeight: brioStyles.fontWeightMedium }}>category</th>
                <th style={{ padding: brioStyles.spacingS, textAlign: 'right', fontWeight: brioStyles.fontWeightMedium }}>projects</th>
                <th style={{ padding: brioStyles.spacingS, textAlign: 'right', fontWeight: brioStyles.fontWeightMedium }}>hours</th>
                <th style={{ padding: brioStyles.spacingS, textAlign: 'right', fontWeight: brioStyles.fontWeightMedium }}>direct cost</th>
                <th style={{ padding: brioStyles.spacingS, textAlign: 'right', fontWeight: brioStyles.fontWeightMedium }}>total cost</th>
                <th style={{ padding: brioStyles.spacingS, textAlign: 'right', fontWeight: brioStyles.fontWeightMedium }}>income</th>
                <th style={{ padding: brioStyles.spacingS, textAlign: 'right', fontWeight: brioStyles.fontWeightMedium }}>profit</th>
                <th style={{ padding: brioStyles.spacingS, textAlign: 'right', fontWeight: brioStyles.fontWeightMedium }}>margin</th>
              </tr>
            </thead>
            <tbody>
              {categoryStats.map((cat, idx) => (
                <tr key={idx} style={{ 
                  borderBottom: `1px solid ${brioColors.border}`,
                  background: idx % 2 === 0 ? brioColors.white : brioColors.greyLight
                }}>
                  <td style={{ padding: brioStyles.spacingS, fontWeight: brioStyles.fontWeightMedium }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <div style={{
                        width: '12px',
                        height: '12px',
                        background: colors[idx % colors.length],
                        borderRadius: '2px'
                      }} />
                      {cat.category}
                    </div>
                  </td>
                  <td style={{ padding: brioStyles.spacingS, textAlign: 'right', color: brioColors.grey }}>
                    {cat.projects}
                  </td>
                  <td style={{ padding: brioStyles.spacingS, textAlign: 'right', color: brioColors.grey }}>
                    {cat.hours}h
                  </td>
                  <td style={{ padding: brioStyles.spacingS, textAlign: 'right', color: brioColors.grey }}>
                    {fmtCurr(cat.cost)}
                  </td>
                  <td style={{ padding: brioStyles.spacingS, textAlign: 'right', color: brioColors.grey }}>
                    {fmtCurr(cat.cost * (1 + essentialProfitPercentage / 100))}
                  </td>
                  <td style={{ padding: brioStyles.spacingS, textAlign: 'right', color: brioColors.gold, fontWeight: brioStyles.fontWeightMedium }}>
                    {fmtCurr(cat.revenue)}
                  </td>
                  <td style={{ padding: brioStyles.spacingS, textAlign: 'right', color: brioColors.black, fontWeight: brioStyles.fontWeightMedium }}>
                    {fmtCurr(cat.profit)}
                  </td>
                  <td style={{ padding: brioStyles.spacingS, textAlign: 'right', color: brioColors.black, fontWeight: brioStyles.fontWeightMedium }}>
                    {cat.margin}%
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr style={{ 
                borderTop: `2px solid ${brioColors.border}`,
                background: brioColors.greyLight,
                fontWeight: brioStyles.fontWeightMedium
              }}>
                <td style={{ padding: brioStyles.spacingS }}>total</td>
                <td style={{ padding: brioStyles.spacingS, textAlign: 'right' }}>
                  {categoryStats.reduce((s, c) => s + c.projects, 0)}
                </td>
                <td style={{ padding: brioStyles.spacingS, textAlign: 'right' }}>
                  {Math.round(categoryStats.reduce((s, c) => s + c.hours, 0))}h
                </td>
                <td style={{ padding: brioStyles.spacingS, textAlign: 'right' }}>
                  {fmtCurr(categoryStats.reduce((s, c) => s + c.cost, 0))}
                </td>
                <td style={{ padding: brioStyles.spacingS, textAlign: 'right' }}>
                  {fmtCurr(categoryStats.reduce((s, c) => s + c.cost * (1 + essentialProfitPercentage / 100), 0))}
                </td>
                <td style={{ padding: brioStyles.spacingS, textAlign: 'right', color: brioColors.gold }}>
                  {fmtCurr(categoryStats.reduce((s, c) => s + c.revenue, 0))}
                </td>
                <td style={{ padding: brioStyles.spacingS, textAlign: 'right', color: brioColors.black }}>
                  {fmtCurr(categoryStats.reduce((s, c) => s + c.profit, 0))}
                </td>
                <td style={{ padding: brioStyles.spacingS, textAlign: 'right' }}>
                  {/* FIX 5: Added division-by-zero guard */}
                  {((() => {
                    const totalRevenue = categoryStats.reduce((s, c) => s + c.revenue, 0);
                    if (totalRevenue <= 0) return '0%';
                    const avg = categoryStats.reduce((s, c) => s + c.margin * c.revenue, 0) / totalRevenue;
                    return Math.abs(avg) < 10 ? `${avg.toFixed(1)}%` : `${Math.round(avg)}%`;
                  })())}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    );
  };

  // ============================================================================
  // SECTION 4: PROJECT REPORTS
  // ============================================================================

  // ProjectReportsSection extracted to ./sections/ProjectReportsSection.jsx

  // ============================================================================
  // SECTION 5: INVOICING
  // ============================================================================

  // InvoicingSection extracted to ./sections/InvoicingSection.jsx

  // ============================================================================
  // SECTION 6: SALARY COST ANALYSIS
  // ============================================================================

  // SalaryCostAnalysisSection extracted to ./sections/SalaryCostAnalysisSection.jsx

  // ============================================================================
  // CASHFLOW PROJECTION SECTION
  // ============================================================================

  // CashflowProjectionSection extracted to ./sections/CashflowProjectionSection.jsx

  // ============================================================================
  // STAGE INTELLIGENCE (placeholder)
  // ============================================================================

  // StageIntelligenceSection extracted to ./sections/StageIntelligenceSection.jsx

  // ============================================================================
  // CAPACITY INTELLIGENCE (placeholder)
  // ============================================================================

  // CapacityIntelligenceSection extracted to ./sections/CapacityIntelligenceSection.jsx

  // ============================================================================
  // SECTION: FINANCIAL OVERVIEW (CEO-level revenue vs cost timeline)
  // ============================================================================

  // FinancialOverviewSection extracted to ./sections/FinancialOverviewSection.jsx

  // ============================================================================
  // MAIN NAVIGATION & LAYOUT
  // ============================================================================

  // Section components are defined inside this component (closure over all
  // props/state), so React sees a *new* function identity on every parent
  // render. That makes React unmount + remount the active section on every
  // re-render, resetting local state and closing open dropdowns.
  //
  // Fix: wrap each section in a stable ref-based proxy created once via
  // useRef. The proxy always delegates to the *current* version of the
  // section function (updated every render), so closure values stay fresh
  // while React keeps the same component identity across renders.

  // Shared scope handed to extracted section components via context.
  const analyticsCtx = {
    CategoryPerformanceChart, activeExpenses, activeOfficeOverheads, activeTab, allProjectInvoices, allTimeEntriesLoaded,
    allocations, annotations, clients, comparisonPaidRevenue, computeOverheadAllocation, currSymbol,
    currentUser, dateRange, essentialProfitPercentage, expandedChart, expensesByProject, exportNotification,
    filterEntries, filterProjects, filteredPaidRevenue, filteredRevenueByProject, fmtCurr, getComparisonData,
    getDateRangeBounds, getFilteredTotalRevenue, getMonthFxDate, getProjectRevenue, handleAddAnnotation, handleDeleteAnnotation,
    handleExportCSV, handleExportPDF, handleToggleAnnotation, hasPaidInvoiceData, money, overheadByProject,
    paidRevenueByMonth, paidRevenueByProjectYear, paidRevenueByYear, pendingDeleteId, projectStatus, projects,
    revenueByProject, billedRevenueByProject, roleHistoryByUser, setActiveTab, setDateRange, setExpandedChart, setExportNotification,
    setShowInactiveHeatmap, settingsHook, showInactiveHeatmap, stageStatusHook, timeEntries, totalRevenueAll,
    users,
  };

  const sections = [
    { id: 'overview', label: 'overview', component: OverviewSection },
    { id: 'timesheets', label: 'timesheets', component: TimesheetsSection },
    { id: 'allocation', label: 'teams', component: TeamAllocationSection },
    { id: 'financial-overview', label: 'financial', component: FinancialOverviewSection },
    { id: 'analytics-projects', label: 'projects', component: ProjectReportsSection },
    { id: 'invoicing', label: 'invoicing', component: InvoicingSection },
    { id: 'cashflow', label: 'cashflow', component: CashflowProjectionSection },
    { id: 'salary', label: 'salary cost analysis', component: SalaryCostAnalysisSection },
    { id: 'stage-intelligence', label: 'stage intelligence', component: StageIntelligenceSection },
    { id: 'capacity-intelligence', label: 'capacity intelligence', component: CapacityIntelligenceSection }
  ];

  const CurrentSection = sections.find(s => s.id === activeSection)?.component;

  return (
    <div 
      className={`brio-dashboard ${expandedChart !== null ? 'brio-dashboard--fullscreen' : ''}`}
      style={{
        minHeight: '100vh',
        background: brioColors.white,
        fontFamily: brioStyles.fontFamily,
        // Full-screen mode overrides
        ...(expandedChart !== null && {
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          width: '100vw',
          height: '100vh',
          zIndex: 9999,
          overflow: 'auto'
        })
      }}
    >
      <style>{`
        .brio-dashboard {
          font-family: ${brioStyles.fontFamily};
        }
        .brio-dashboard a {
          color: ${brioColors.gold};
          text-decoration: none;
        }
        .brio-dashboard a:hover {
          color: ${brioColors.goldHover};
        }
        
        /* ================================================================
           GLOBAL: Hide main app navigation when Analytics is in full-screen
           This class is added to <body> by the Analytics component
           ================================================================ */
        body.analytics-fullscreen-active > div > nav,
        body.analytics-fullscreen-active > div > header:not(.analytics-header),
        body.analytics-fullscreen-active > div > .app-header,
        body.analytics-fullscreen-active > div > .app-nav,
        body.analytics-fullscreen-active > div > .main-navigation,
        body.analytics-fullscreen-active > div > .top-navigation,
        body.analytics-fullscreen-active > div > .sidebar,
        body.analytics-fullscreen-active .sticky.top-0:not(.analytics-header):not(.analytics-nav),
        body.analytics-fullscreen-active .sticky.top-\\[60px\\]:not(.analytics-nav) {
          display: none !important;
        }
        
        /* Hide the two-row navigation in App.js when fullscreen */
        body.analytics-fullscreen-active .bg-white.shadow-sm.sticky,
        body.analytics-fullscreen-active [class*="sticky"][class*="top-0"]:not(.brio-dashboard):not(.brio-dashboard *),
        body.analytics-fullscreen-active [class*="sticky"][class*="top-"]:not(.brio-dashboard):not(.brio-dashboard *) {
          display: none !important;
        }
        
        /* Ensure Analytics takes full viewport when fullscreen */
        body.analytics-fullscreen-active .brio-dashboard--fullscreen {
          position: fixed !important;
          top: 0 !important;
          left: 0 !important;
          right: 0 !important;
          bottom: 0 !important;
          z-index: 9999 !important;
        }
        
        /* Full-screen mode styles for Analytics dashboard */
        .brio-dashboard--fullscreen {
          /* Reduced spacing in full-screen */
        }
        .brio-dashboard--fullscreen .analytics-header {
          padding: 10px 16px;
        }
        .brio-dashboard--fullscreen .analytics-main {
          max-width: none;
          padding: 12px 16px;
        }
        .brio-dashboard--fullscreen .analytics-footer {
          display: none;
        }
        
        /* Full-screen close button */
        .analytics-fullscreen-close {
          position: fixed;
          top: 8px;
          right: 16px;
          z-index: 10001;
          width: 32px;
          height: 32px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: ${brioColors.white};
          border: 1px solid ${brioColors.border};
          border-radius: 4px;
          cursor: pointer;
          font-size: 16px;
          color: ${brioColors.grey};
          transition: all 0.15s ease;
          box-shadow: 0 1px 3px rgba(0,0,0,0.1);
        }
        .analytics-fullscreen-close:hover {
          background: ${brioColors.greyLight};
          color: ${brioColors.black};
          border-color: ${brioColors.grey};
        }
        
        /* Full-screen toggle button */
        .analytics-fullscreen-toggle {
          padding: 6px 12px;
          background: transparent;
          border: 1px solid ${brioColors.border};
          color: ${brioColors.grey};
          font-size: 11px;
          font-family: ${brioStyles.fontFamily};
          font-weight: ${brioStyles.fontWeightRegular};
          cursor: pointer;
          text-transform: lowercase;
          border-radius: 0;
          display: flex;
          align-items: center;
          gap: 6px;
          transition: all 0.15s ease;
        }
        .analytics-fullscreen-toggle:hover {
          border-color: ${brioColors.gold};
          color: ${brioColors.gold};
        }
      `}</style>
      
      {/* Full-screen close button - only visible in full-screen mode */}
      {expandedChart !== null && (
        <button
          className="analytics-fullscreen-close"
          onClick={() => setExpandedChart(null)}
          title="Exit full screen (Esc)"
          aria-label="Exit full screen"
        >
          ✕
        </button>
      )}
      
      {/* Content Area */}
      <main 
        className="analytics-main"
        style={{
          maxWidth: expandedChart !== null ? 'none' : brioStyles.maxWidthContainer,
          margin: '0 auto',
          padding: expandedChart !== null ? '16px' : brioStyles.spacingXL
        }}
      >
        {CurrentSection && (
          <AnalyticsContext.Provider value={analyticsCtx}>
            <Suspense fallback={<div style={{ padding: brioStyles.spacingXL, color: brioColors.grey, fontSize: brioStyles.fontSizeBase }}>loading…</div>}>
              <CurrentSection />
            </Suspense>
          </AnalyticsContext.Provider>
        )}
      </main>

      {/* Footer - hidden in full-screen mode */}
      {expandedChart === null && (
        <footer 
          className="analytics-footer"
          style={{
            borderTop: `1px solid ${brioColors.border}`,
            padding: brioStyles.spacingM,
            textAlign: 'center',
            color: brioColors.grey,
            fontSize: '11px',
            fontWeight: brioStyles.fontWeightLight,
            fontStyle: 'italic'
          }}
        >
          architecture brio • analytics dashboard • decision-support system
        </footer>
      )}
    </div>
  );
}
